import { DELETED_AGENT_NAME, agentDisplayName, isDeletedCyboBinding } from "./agent-display.js";
import { isGroupDm } from "./channel-visibility.js";
import { workspaceState } from "./core/state.svelte.js";

// L16: name AND id, in keys the relay keeps (client-log.ts, projectOrDrop); a person by id only.
export type ReportSubject = Record<string, string>;

type Cybos = ReadonlyArray<{ id: string; name: string }>;

function defined(fields: Record<string, string | undefined>): ReportSubject {
  const out: ReportSubject = {};
  for (const [key, value] of Object.entries(fields)) if (value) out[key] = value;
  return out;
}

export function workspaceSubject(workspaceId: string | null | undefined): ReportSubject {
  if (!workspaceId) return {};
  const current = workspaceState.current?.id === workspaceId ? workspaceState.current : undefined;
  const ws = current ?? workspaceState.list.find((w) => w.id === workspaceId);
  return defined({ workspaceId, workspaceName: ws?.name });
}

// A group DM's name joins its members' names (group-dm.ts): it goes by kind and id only.
export function channelSubject(channelId: string): ReportSubject {
  const channel = workspaceState.channels.find((c) => c.id === channelId);
  if (channel && isGroupDm(channel)) return { channelId, channelKind: "group_dm" };
  return defined({ channelId, channelName: channel?.name });
}

// A cybo as `cyboId` (`agentId` is always a session id); a person or unplaced peer as `peerUserId`.
export function dmPeerSubject(peerId: string, cybos: Cybos): ReportSubject {
  const cybo = cybos.find((c) => c.id === peerId);
  if (cybo) return defined({ cyboId: peerId, peerKind: "agent", agentName: cybo.name });
  const person = workspaceState.members.some((m) => m.userId === peerId);
  return defined({ peerUserId: peerId, peerKind: person ? "person" : undefined });
}

// The machine by id only (`daemonId` is relay-reserved); its label can name its owner.
export function agentSubject(agentId: string, cybos: Cybos, daemonId?: string): ReportSubject {
  const row = workspaceState.agents.find((a) => a.agentId === agentId);
  const machine = daemonId ?? (typeof row?.daemonId === "string" ? row.daemonId : undefined);
  const shown = row ? agentDisplayName(row, cybos) : undefined;
  const name = shown === agentId.slice(0, 10) ? undefined : shown; // an id prefix is no name
  const deleted = !!row && isDeletedCyboBinding(row, cybos) && name !== DELETED_AGENT_NAME;
  return defined({
    agentId,
    agentName: deleted ? `${name} (deleted)` : name,
    client_daemon_id: machine,
  });
}
